import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import { BehaviorSubject, Observable, Subscription, SubscriptionLike, combineLatest, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilKeyChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { FwLegacyDialogService as DialogService } from '../../dialogs/dialog.service';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { ModifyTagsDialog } from '../../sessions/modify-tags-dialog/modify-tags-dialog.component';
import { SessionDetailDialog } from '../../sessions/session-detail/session-detail-dialog.component';
import { ContainerDeletionResults, ContainerMetadata, SessionService } from '../../sessions/session.service';
import { downloadFile } from '../../shared/browser.utils';
import { OverflowMeasurement, calcChipOverflow } from '../../shared/chips.utils';
import { stringToHexColor } from '../../shared/color.utils';
import { getContainerProjectId } from '../../shared/container.utils';
import { ageInYears as _ageInYears, ageInSeconds } from '../../shared/date.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { FileAction } from '../../shared/enums/file.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { getFileClassification } from '../../shared/file.utils';
import { DownloadFile } from '../../shared/models/download.model';
import { SearchFilter, SearchReturnType } from '../../shared/models/search/search-query.model';
import { SearchResult } from '../../shared/models/search/search-results.model';
import { replayLocation } from '../../shared/observable.utils';
import { SidebarService } from '../../sidebar.service';
import { TagFilter, TagSelectMenuComponent } from '../tag-select-menu/tag-select-menu.component';
import { TrainingSetDialog, TrainingSetPayload } from '../training-set/training-set-dialog.component';
import { TranslationService } from '../../translation.service';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';
import { FwAlertModule } from '@flywheel-io/vision';
import { SearchStatusString } from '../../shared/models/search/search-status.model';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { ProjectsQueryService } from 'app/src/projects/projects-query.service';

/* eslint-disable @typescript-eslint/naming-convention */
enum TabNameIndex {
  subjects,
  sessions,
  acquisitions,
  files,
  analyses,
}

enum DownloadTooltipMessageKey {
  'NONE' = 'none',
  'EMPTY' = 'empty',
  'MAXED' = 'maxed'
}
/* eslint-enable @typescript-eslint/naming-convention */

const tabResultType = {
  sessions: SearchReturnType.Session,
  acquisitions: SearchReturnType.Acquisition,
  files: SearchReturnType.File,
  analyses: SearchReturnType.Analysis,
  subjects: SearchReturnType.Subject,
};

// We might want to replace it with an injection token in the future
const MAX_DOWNLOAD_SIZE = 10_000;

interface PageInfo {
  checked: number
  size: number
  total: number
}

interface TableFilters {
  acquisitionLabel: string
  acquisitionModality: string
  acquisitionTimestamp: string
  analysisLabel: string
  analysisTimestamp: string
  fileClassifications: string
  fileContainerType: string
  fileName: string
  fileTimestamp: string
  fileType: string
  gearName: string
  gearVersion: string
  projectLabel: string
  sessionAge: string
  sessionLabel: string
  sessionModality: string
  sessionTimestamp: string
  subjectCode: string
  subjectCohort: string
  subjectMlSet: string
  subjectSex: string
  subjectTimestamp: string
  tags: TagFilter
}

interface ResultActions {
  downloadFiles: ActionState
  addToCollection: ActionState
  runGear: ActionState
  deleteContainers: ActionState
  deleteFiles: ActionState
  moveContainers: ActionState
  createContainer: ActionState
  createTrainingSet: ActionState
  modifyTags: ActionState
}

interface ActionState {
  visible: boolean
  disabled?: boolean
  tooltip?: string
}

interface Hierarchy {
  short: string
  full: string
  pretty: string
}

@Component({
  host: {
    'class': 'page-content',
    'test-id': 'search-results',
  },
  selector: 'search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FileModule,
    FormsModule,
    PipesModule,
    ReactiveFormsModule,
    MaterialModule,
    SharedModule,
    FwAlertModule,
    TagSelectMenuComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultsComponent implements OnDestroy {
  @ViewChild('hierarchyHeaderCell', { read: ElementRef })
  headerCellElement: ElementRef

  allChecked$: Observable<boolean>
  extendChecked$ = new BehaviorSubject(false)
  availableActions$: Observable<ResultActions>
  checkedResultIds$ = new BehaviorSubject(new Set<string>())
  emptyTagsValue = { contains: [], excludes: [] } as TagFilter
  features = {
    tagging: this.config.isFeatureEnabled('tagging'),
  }
  fileActionFeatures = [
    FileAction.Information,
    FileAction.Download,
    FileAction.Delete,
    FileAction.VersionHistory,
    FileAction.View,
  ]
  searchFilters = new FormGroup({
    acquisitionLabel: new FormControl(''),
    acquisitionModality: new FormControl(''),
    acquisitionTimestamp: new FormControl(''),
    analysisLabel: new FormControl(''),
    analysisTimestamp: new FormControl(''),
    fileName: new FormControl(''),
    fileClassifications: new FormControl(''),
    fileContainerType: new FormControl(''),
    fileTimestamp: new FormControl(''),
    fileType: new FormControl(''),
    hierarchy: new FormControl(''),
    gearName: new FormControl(''),
    gearVersion: new FormControl(''),
    projectLabel: new FormControl(''),
    sessionAge: new FormControl(''),
    sessionLabel: new FormControl(''),
    sessionModality: new FormControl(''),
    sessionTimestamp: new FormControl(''),
    subjectTimestamp: new FormControl(''),
    subjectCode: new FormControl(''),
    subjectCohort: new FormControl(''),
    subjectMlSet: new FormControl(''),
    subjectSex: new FormControl(''),
    tags: new FormControl({ contains: [], excludes: [] } as TagFilter),
  } as { [key in keyof TableFilters]: FormControl<TableFilters[key]> | FormGroup })
  searchResults$ = this.sidebar.search.results$.pipe(
    tap(results => {
      // We want to keep checked rows as a subset of results, not a superset
      const resultIds = new Set(results.map(result => result._id));
      const checkedIds = new Set(this.checkedResultIds$.value);
      for (const id of checkedIds) {
        if (!resultIds.has(id)) {
          checkedIds.delete(id);
        }
      }
      const charsThatFit = this.estimateHierarchySize();
      for (const result of results) {
        if (!this.hierarchyMap.has(result)) {
          this.hierarchyMap.set(result, this.getHierarchy(result, charsThatFit));
        }
        if (result._source.file?.classification && !this.classificationMap.has(result)) {
          let classification = getFileClassification(result._source.file);
          classification = classification === 'N/A' ? '' : classification;
          this.classificationMap.set(result, classification);
        }
      }
      this.checkedResultIds$.next(checkedIds);
      this.isLoading = false;
    }),
    map(results => results.slice(0, this.sidebar.search.pageSize)),
  )
  selectedResultId: string = null
  tabIndex = TabNameIndex.subjects
  currentResultModal: MatDialogRef<SessionDetailDialog> = null
  ageInYears = _ageInYears
  hierarchyMap = new WeakMap<SearchResult, Hierarchy>()
  classificationMap = new WeakMap<SearchResult, string>()
  tagOverflows: OverflowMeasurement[] = []
  truncateText = true
  downloading = false
  downloadIsDisabled = true
  private _defaultContainerCounts = {
    [SearchReturnType.Subject]: '',
    [SearchReturnType.Session]: '',
    [SearchReturnType.Acquisition]: '',
    [SearchReturnType.File]: '',
    [SearchReturnType.Analysis]: '',
  };
  searchResultCounts$: BehaviorSubject<Record<SearchReturnType, string>> = new BehaviorSubject<Record<SearchReturnType, string>>(this._defaultContainerCounts);
  searchStatus$: Observable<SearchStatusString> = this.coreApi.dataexplorer.status().pipe(
    map(res => res.status),
    catchError(error => {
      this.logger.error(error);
      this.notifications.error('Could not fetch Data Explorer status.', true);
      return of('up-to-date' as SearchStatusString);
    }),
    shareReplay(1),
  )

  displayStatusWarning$ = this.searchStatus$.pipe(
    map(status => status !== 'up-to-date')
  );

  searchStatusMessage$ = this.searchStatus$.pipe(
    map(status => {
      const statusToMessage: { [k in SearchStatusString]: string } = {
        'error': 'Data explorer is in an error state. Search results may be unavailable or incomplete.',
        'reindex': 'Site data is being reindexed. Search results may be unavailable, slow, and incomplete.',
        'indexing': 'Site data is being indexed. Search results may be unavailable or incomplete.',
        'up-to-date': '',
      }
      return statusToMessage[status];
    })
  )


  private subscriptions = {
    checks: Subscription.EMPTY,
    location: Subscription.EMPTY as SubscriptionLike,
    reload: Subscription.EMPTY,
    resultModal: Subscription.EMPTY,
    resultCount: Subscription.EMPTY,
    startSearch: Subscription.EMPTY,
  }

  private _cachedPage: PageInfo;
  private _lastTotal = 0;
  private _lastCheckedSize = 0;

  downloadTooltipMessage = 'none';
  isLoading = true;

  constructor(
    private config: ConfigService,
    private dialog: DialogService,
    private element: ElementRef,
    private coreApi: CoreApiService,
    location: Location,
    private logger: LoggingService,
    private notifications: NotificationService,
    private projects: ProjectsService,
    private projectQueryService: ProjectsQueryService,
    private queryClient: QueryClient,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private sessions: SessionService,
    private translate: TranslationService,
    readonly sidebar: SidebarService,
    private cd: ChangeDetectorRef,
  ) {
    this.availableActions$ = combineLatest([
      this.checkedResultIds$,
      this.sidebar.search.resultType$,
      this.projects.getProjectsWithPermissions([PermissionAction.ContainersCreateHierarchy]),
      this.projects.getLockedProjectIds(),
      this.translate.getBundle('searchResults.actionTooltips'),
    ]).pipe(
      switchMap(([checkedIds, resultType, writableProjects, lockedProjects, messages]) => {
        const checkedResults = this.getCheckedResults(checkedIds);
        const deviceFilesChecked = checkedResults.some(result => result._source.file?.origin?.type === 'device');
        const noneChecked = checkedIds.size === 0;
        const selectedLockedProjects = checkedResults.some(result => lockedProjects.includes(getContainerProjectId(result)));
        return this.projects.reducePermissions(checkedResults).pipe(
          map(permissionActions => {
            const permissions = new Set(permissionActions);
            return {
              downloadFiles: {
                visible: [SearchReturnType.Subject, SearchReturnType.Session, SearchReturnType.Acquisition, SearchReturnType.Analysis, SearchReturnType.File].includes(resultType),
                disabled: noneChecked || !permissions.has(PermissionAction.FilesDownload),
                tooltip: noneChecked ? messages.get('downloadFiles.empty') : messages.get('downloadFiles.permission'),
              },
              addToCollection: {
                visible: config.isFeatureEnabled('collections') && [SearchReturnType.Session, SearchReturnType.Acquisition].includes(resultType),
                disabled: noneChecked,
                tooltip: messages.get('addToCollection.empty'),
              },
              runGear: {
                visible: SearchReturnType.Session === resultType,
                disabled: noneChecked || !permissions.has(
                  this.config.isFeatureEnabled('job_prioritization')
                    ? PermissionAction.JobsManage
                    : PermissionAction.JobsRunCancel
                ),
                tooltip: noneChecked ? messages.get('runGear.empty')
                  : selectedLockedProjects ? messages.get('runGear.locked')
                    : messages.get('runGear.permission'),
              },
              deleteContainers: {
                visible: [SearchReturnType.Session, SearchReturnType.Acquisition].includes(resultType),
                disabled: noneChecked || !permissions.has(PermissionAction.ContainersDeleteHierarchy),
                tooltip: noneChecked ? messages.get('deleteContainers.empty')
                  : selectedLockedProjects ? messages.get('deleteContainers.locked')
                    : messages.get('deleteContainers.permission'),
              },
              deleteFiles: {
                visible: SearchReturnType.File === resultType,
                disabled: noneChecked || !permissions.has(deviceFilesChecked ? PermissionAction.FilesDeleteDeviceData : PermissionAction.FilesDeleteNonDeviceData),
                tooltip: noneChecked ? messages.get('deleteFiles.empty')
                  : selectedLockedProjects ? messages.get('deleteFiles.locked')
                    : messages.get('deleteFiles.permission'),
              },
              moveContainers: {
                visible: SearchReturnType.Session === resultType,
                disabled: noneChecked || writableProjects.length === 0 || !permissions.has(PermissionAction.ContainersDeleteHierarchy),
                tooltip: noneChecked ? messages.get('moveContainers.empty')
                  : selectedLockedProjects ? messages.get('moveContainers.locked')
                    : messages.get('moveContainers.permission'),
              },
              createContainer: {
                visible: SearchReturnType.Session === resultType,
                disabled: writableProjects.length === 0,
                tooltip: messages.get('createContainer.permission'),
              },
              createTrainingSet: {
                visible: SearchReturnType.File === resultType,
                disabled: false,
                tooltip: null,
              },
              modifyTags: {
                visible: true,
                disabled: noneChecked || !permissions.has(PermissionAction.TagsManage),
                tooltip: noneChecked ? messages.get('modifyTags.empty') : messages.get('modifyTags.permission'),
              },
            };
          })
        );
      })
    );
    this.subscriptions.checks = combineLatest([
      this.sessions.checkedSubjects$,
      this.sessions.checkedSessions$,
    ]).subscribe(([checkedSubjects, checkedSessions]) => {
      // Feed session checked status into the Session/Acquisition results
      if (this.resultType === SearchReturnType.Subject) {
        const resultIds = this.sidebar.search.results$.value
          .filter(result => checkedSubjects.get(result._source.subject?._id)?.checked)
          .map(result => result._id);
        this.checkedResultIds$.next(new Set(resultIds));
      } else if (this.resultType === SearchReturnType.Session) {
        const resultIds = this.sidebar.search.results$.value
          .filter(result => checkedSessions.get(result._source.session?._id)?.checked)
          .map(result => result._id);
        this.checkedResultIds$.next(new Set(resultIds));
      } else if (this.resultType === SearchReturnType.Acquisition) {
        const resultIds = this.sidebar.search.results$.value
          .filter(result => {
            const status = checkedSessions.get(result._source.session?._id);
            return status?.checked && (!status.indeterminate || status.acquisitions.has(result._source.acquisition?._id));
          })
          .map(result => result._id);
        this.checkedResultIds$.next(new Set(resultIds));
      }
    });
    this.allChecked$ = combineLatest([
      this.sidebar.search.results$,
      this.checkedResultIds$,
    ]).pipe(
      map(([results, checkedIds]) => this.extendChecked$.value || (results.length > 0 && results.every(result => checkedIds.has(result._id))))
    );
    this.sidebar.search.sessionTimestamp$.subscribe(timestamp => {
      this.searchFilters.get('sessionTimestamp').setValue(timestamp, { emitEvent: false });
    });
    this.subscriptions.reload = merge(
      this.searchFilters.valueChanges.pipe(debounceTime(500)),
      // Elastic is not going to pick up deletions right away so the containers
      // will remain in search results, but at least we tried.
      this.sessions.sessionDeleted$,
      this.sessions.subjectDeleted$,
    ).subscribe(() => this.filterResults());
    this.subscriptions.location = replayLocation(location).pipe(
      distinctUntilKeyChanged('url'),
      debounceTime(50),
    ).subscribe(() => {
      this.captureRouteState();
    });
    this.subscriptions.startSearch = this.sidebar.search.searchStart$.subscribe(() => {
      this.isLoading = true;
    });

    this.subscriptions.resultCount = this.sidebar.search.results$.pipe(
      map(() => {
        const counts = { ...this._defaultContainerCounts };
        for (const key of Object.keys(counts)) {
          const { value } = this.sidebar.search.response?.facets?.facets?.[key + '_count'] || {};
          if (value !== undefined && typeof value === 'number') {
            counts[key] = `(${this.sidebar.formatCountAsEstimated(value)})`;
            continue;
          }
          counts[key] = '';
        }
        return counts;
      })
    ).subscribe((counts) => {
      this.searchResultCounts$.next(counts);
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
    if (this.currentResultModal) {
      this.currentResultModal.close();
      this.currentResultModal = null;
    }
  }

  ngAfterContentChecked(): void {
    const items = this.element.nativeElement.querySelectorAll('.chip-list');
    this.tagOverflows = calcChipOverflow(items);
  }

  @HostListener('window:resize', ['$event'])
  calcHierarchy(): void {
    const results = this.sidebar.search.results$.value;
    const charsThatFit = this.estimateHierarchySize();
    for (const result of results) {
      this.hierarchyMap.set(result, this.getHierarchy(result, charsThatFit));
    }
  }

  get resultType(): SearchReturnType {
    return this.sidebar.search.resultType$.value;
  }

  get page(): PageInfo {
    const { pageSize, response } = this.sidebar.search;
    const { value: total = 0 } = response?.facets?.facets?.[this.resultType + '_count'] ?? {};

    if (this._lastTotal !== total || this._lastCheckedSize !== this.checkedResultIds$.value.size) {
      this._lastTotal = total;
      this._lastCheckedSize = this.checkedResultIds$.value.size;

      const payload = {
        checked: this._lastCheckedSize,
        size: Math.min(pageSize, total),
        total: total,
      };

      this.downloadButtonStatus(total, this._lastCheckedSize);

      this._cachedPage = payload;
    }

    return this._cachedPage;
  }

  get resultColumns(): string[] {
    switch (this.resultType) {
      case SearchReturnType.Session:
        return ['checkbox', 'sessionTimestamp', 'hierarchy', 'sessionLabel', 'sessionModality', 'sessionAge', 'subjectSex', 'subjectCohort', 'subjectMlSet', 'tags'];
      case SearchReturnType.Acquisition:
        return ['checkbox', 'acquisitionTimestamp', 'hierarchy', 'acquisitionLabel', 'acquisitionModality', 'subjectCohort', 'subjectMlSet', 'tags'];
      case SearchReturnType.File:
        return ['checkbox', 'fileTimestamp', 'hierarchy', 'fileName', 'fileContainerType', 'fileType', 'fileClassifications', 'tags', 'fileActions'];
      case SearchReturnType.Analysis:
        return ['checkbox', 'analysisTimestamp', 'hierarchy', 'analysisLabel', 'gearName', 'gearVersion', 'tags'];
      case SearchReturnType.Subject:
        return ['checkbox', 'subjectTimestamp', 'hierarchy', 'subjectCode', 'subjectSex', 'subjectCohort', 'subjectMlSet', 'tags'];
      default:
        return [];
    }
  }

  get checkedStatusColumns(): string[] {
    if (this.isLoading) {
      return ['loadingIndicator'];
    }
    else if (this.page?.checked < this.page?.total && this.page?.checked > 0) {
      return ['extendChecked'];
    }
    else if (this.page?.checked > 0) {
      return ['allChecked'];
    }
    // this prevents headers from changing when results are filtered down to none
    // which was blurring filter inputs
    return [];
  }

  get selectedResult(): SearchResult {
    return this.sidebar.search.results$.value.find(result => result._id === this.selectedResultId);
  }

  getHierarchy(row: SearchResult, targetLength = 60): Hierarchy {
    const { group, project, subject, session, acquisition } = row._source;
    const parents = { group: group.label || '', project: project.label || '', subject: subject?.code || '', session: session?.label || '', acquisition: acquisition?.label || '' };
    const result = { short: '', full: '', pretty: '' };

    let parts = [];
    result.pretty = `Group: ${parents.group}\nProject: ${parents.project}`;
    switch (this.resultType) {
      case SearchReturnType.Session:
        parts = [parents.group, parents.project, parents.subject || ''];
        if (parents.subject) {
          result.pretty += `\nSubject: ${parents.subject}`;
        }
        break;
      case SearchReturnType.Subject:
        parts = [parents.group, parents.project];
        break;
      case SearchReturnType.Acquisition:
        parts = [parents.group, parents.project, parents.subject || '', parents.session || ''];
        if (parents.subject) {
          result.pretty += `\nSubject: ${parents.subject}`;
        }
        if (parents.session) {
          result.pretty += `\nSession: ${parents.session}`;
        }
        break;
      case SearchReturnType.File:
        parts = [parents.group, parents.project, parents.subject || '', parents.session || '', parents.acquisition || ''];
        if (parents.subject) {
          result.pretty += `\nSubject: ${parents.subject}`;
        }
        if (parents.session) {
          result.pretty += `\nSession: ${parents.session}`;
        }
        if (parents.acquisition) {
          result.pretty += `\nAcquisition: ${parents.acquisition}`;
        }
        break;
      case SearchReturnType.Analysis:
        parts = [parents.group, parents.project, parents.subject || '', parents.session || ''];
        if (parents.subject) {
          result.pretty += `\nSubject: ${parents.subject}`;
        }
        if (parents.session) {
          result.pretty += `\nSession: ${parents.session}`;
        }
        break;
    }
    parts = parts.filter(part => Boolean(part));
    result.full = parts.join(' > ');

    if (parts.join(' > ').length > targetLength) {
      let shortened = '';
      for (const [i, part] of parts.entries()) {
        // skip empty string parts
        if (!part || part === '') {
          continue;
        }
        const estimatedRemainingChars = parts.slice(i).join(' > ').length;
        const shouldTruncate = (shortened.length + estimatedRemainingChars) > targetLength && part.length > 9;
        const emptyMiddle = part.slice(0, 3) + '...' + part.slice(-3);
        if (i === parts.length - 1) {
          shortened += ` > ${part}`; // Never truncate last part
        }
        else if (i === 0) {
          shortened += `${shouldTruncate ? emptyMiddle : part}`;
        }
        else {
          shortened += ` > ${shouldTruncate ? emptyMiddle : part}`;
        }
      }
      result.short = shortened;
    }
    else {
      result.short = result.full;
    }
    return result;
  }

  estimateHierarchySize(): number {
    let charsThatFit: number;
    if (this.headerCellElement) {
      const headerWidth = Number(this.headerCellElement.nativeElement.offsetWidth);
      const sampleWidth = 58; // width of 10 average characters. check my math if you like https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
      charsThatFit = Math.floor(headerWidth / (sampleWidth / 10));
    }
    return charsThatFit;
  }

  private captureRouteState(): void {
    const selectedTab = this.activatedRoute.snapshot.params.searchTab || TabNameIndex[this.tabIndex];
    // update the session details tab to match our tab
    this.sessions.lastSessionDetailsTab = this.detailTabForResultType(selectedTab);
    this.tabIndex = TabNameIndex[selectedTab as string] || 0;
    const resultType = tabResultType[selectedTab];
    if (this.sidebar.search.context !== resultType && this.sidebar.search.response) {
      this.checkAllRows(false);
      this.sidebar.search.results$.next([]);
      this.sidebar.search.resultType$.next(resultType);
      this.sidebar.search.context = resultType;
      this.filterResults();
    } else {
      // Sidebar applies its filters and does the initial search.
      // Only set the context and delegate search to the sidebar.
      this.sidebar.search.context = resultType;
    }
  }

  changeTab(change?: MatTabChangeEvent): void {
    const selectedTab = TabNameIndex[change?.index ?? this.tabIndex];

    // update the session details tab to match our tab
    this.sessions.lastSessionDetailsTab = this.detailTabForResultType(selectedTab);
    if (this.activatedRoute.snapshot.params.searchTab !== selectedTab) {

      this.router.navigate(['search', selectedTab], {
        queryParamsHandling: 'preserve',
      });

      const resultType = tabResultType[selectedTab];
      if (this.sidebar.search.context !== resultType) {
        this.checkAllRows(false);
        this.sidebar.search.results$.next([]);
        this.sidebar.search.resultType$.next(resultType);
        this.sidebar.search.context = resultType;
        this.filterResults();
        this.sidebar.search.acqId$.next('');
        this.sidebar.search.analysisLabel$.next('');
        this.sidebar.search.selectedContainer$.next(null);
      }
    }
  }

  changePageSize(size: number): void {
    this.sidebar.search.pageSize = size;
    this.page.size = size;
    this.filterResults();
  }

  downloadButtonStatus(total: number, checkedSize: number): void {
    const haveTotal = Boolean(total);

    if (haveTotal) {
      (total < MAX_DOWNLOAD_SIZE) ? this.downloadIsDisabled = false : this.downloadIsDisabled = true;

      if (!checkedSize) {
        this.downloadTooltipMessage = DownloadTooltipMessageKey.EMPTY;
      }

      if (total > MAX_DOWNLOAD_SIZE) {
        this.downloadTooltipMessage = DownloadTooltipMessageKey.MAXED;
      }
    } else {
      this.downloadIsDisabled = true;
      this.downloadTooltipMessage = DownloadTooltipMessageKey.NONE;
    }
  }

  getTags(element: SearchResult): string[] {
    switch (this.resultType) {
      case SearchReturnType.Subject:
        return sortBy(element._source.subject?.tags || [], 'length');
      case SearchReturnType.Session:
        return sortBy(element._source.session?.tags || [], 'length');
      case SearchReturnType.Acquisition:
        return sortBy(element._source.acquisition?.tags || [], 'length');
      case SearchReturnType.File:
        return sortBy(element._source.file?.tags || [], 'length');
      case SearchReturnType.Analysis:
        return sortBy(element._source.analysis?.tags || [], 'length');
      default:
        return [];
    }
  }

  @HostListener('window:resize', ['$event'])
  public calcOverflows(): void {
    const items = this.element.nativeElement.querySelectorAll('.chip-list');
    this.tagOverflows = calcChipOverflow(items);
  }

  updateTagsFilter(tags: TagFilter): void {
    this.searchFilters.get('tags').setValue(tags);
  }

  /**
   * For mat-table trackBy
   */
  getResultRowId(index: number, result: SearchResult): string {
    return result._id;
  }

  filterResults(): void {
    const filters = this.searchFilters.value;
    const tableFilters: SearchFilter[] = [];
    const tagFilterPath = `${this.resultType}.tags`;
    // These appear in multiple tabs...
    if (filters.subjectCohort) {
      tableFilters.push({
        match: {
          'subject.cohort': filters.subjectCohort,
        },
      });
    }
    // remove the tags filters
    this.sidebar.search.structuredQuery = (this.sidebar.search.structuredQuery || '').replace(/\s?(OR)?\s?(AND)?\s?(NOT)?\s?\w+\.tags IN \[[^\]]+\]/g, '');
    if (filters?.tags?.contains?.length > 0 || filters?.tags?.excludes?.length > 0) {
      const tagsFilters = [];
      if (filters?.tags?.contains?.length > 0) {
        tagsFilters.push(`${tagFilterPath} IN [${filters.tags.contains.join(', ')}] `);
      }
      if (filters?.tags?.excludes?.length > 0) {
        tagsFilters.push(`NOT ${tagFilterPath} IN [${filters.tags.excludes.join(', ')}]`);
      }
      let seperator = '';
      if (this.sidebar.search.structuredQuery.length > 0) {
        seperator = ' AND ';
      }
      this.sidebar.search.structuredQuery = (this.sidebar.search.structuredQuery + seperator + tagsFilters.join(' AND ')).trim();
    }
    if (filters.subjectMlSet) {
      tableFilters.push({
        match: {
          'subject.mlset': filters.subjectMlSet,
        },
      });
    }

    if (this.resultType === SearchReturnType.Subject) {
      if (filters.subjectTimestamp) {
        tableFilters.push({
          range: {
            'subject.created': getDayRange(filters.subjectTimestamp),
          },
        });
      }
      if (filters.subjectCode) {
        tableFilters.push({
          match: {
            'subject.code': filters.subjectCode,
          },
        });
      }
      if (filters.subjectSex) {
        tableFilters.push({
          term: {
            'subject.sex': filters.subjectSex,
          },
        });
      }
      if (filters.subjectCohort) {
        tableFilters.push({
          match: {
            'subject.cohort': filters.subjectCohort,
          },
        });
      }
      if (filters.subjectMlSet) {
        tableFilters.push({
          match: {
            'subject.mlset': filters.subjectMlSet,
          },
        });
      }
    }
    if (this.resultType === SearchReturnType.Session) {
      if (filters.sessionTimestamp) {
        tableFilters.push({
          range: {
            'session.timestamp': getDayRange(filters.sessionTimestamp),
          },
        });
      }
      if (filters.sessionLabel) {
        tableFilters.push({
          match: {
            'session.label': filters.sessionLabel,
          },
        });
      }
      if (filters.sessionAge) {
        tableFilters.push({
          match: {
            'session.age': ageInSeconds(Number(filters.sessionAge)).toString(),
          },
        });
      }
      if (filters.sessionModality) {
        tableFilters.push({
          match: {
            'file.modality': filters.sessionModality,
          },
        });
      }
      if (filters.subjectSex) {
        tableFilters.push({
          term: {
            'subject.sex': filters.subjectSex.toLowerCase(),
          },
        });
      }
    }
    if (this.resultType === SearchReturnType.Acquisition) {
      if (filters.acquisitionTimestamp) {
        tableFilters.push({
          range: {
            'acquisition.timestamp': getDayRange(filters.acquisitionTimestamp),
          },
        });
      }
      if (filters.acquisitionLabel) {
        tableFilters.push({
          term: {
            'acquisition.label': filters.acquisitionLabel,
          },
        });
      }
      if (filters.acquisitionModality) {
        tableFilters.push({
          term: {
            'file.modality': filters.acquisitionModality,
          },
        });
      }
    }
    if (this.resultType === SearchReturnType.File) {
      if (filters.fileName) {
        tableFilters.push({
          match: {
            'file.name': filters.fileName,
          },
        });
      }
      if (filters.fileType) {
        tableFilters.push({
          match: {
            'file.type': filters.fileType,
          },
        });
      }
      if (filters.fileContainerType) {
        tableFilters.push({
          match: {
            'file.parent_ref.type': filters.fileContainerType,
          },
        });
      }
      if (filters.fileClassifications) {
        tableFilters.push({
          match: {
            'file.classification': filters.fileClassifications,
          },
        });
      }
    }
    if (this.resultType === SearchReturnType.Analysis) {
      if (filters.analysisLabel) {
        tableFilters.push({
          match: {
            'analysis.label': filters.analysisLabel,
          },
        });
      }
      if (filters.gearName) {
        tableFilters.push({
          match: {
            'analysis.gear_info.name': filters.gearName,
          },
        });
      }
      if (filters.gearVersion) {
        tableFilters.push({
          match: {
            'analysis.gear_info.version.raw': filters.gearVersion,
          },
        });
      }
      if (filters.analysisTimestamp) {
        tableFilters.push({
          range: {
            'analysis.created': getDayRange(filters.analysisTimestamp),
          },
        });
      }
    }
    if (filters.projectLabel && this.resultType !== SearchReturnType.Acquisition) {
      tableFilters.push({
        match: {
          'project.label': filters.projectLabel,
        },
      });
    }
    this.sidebar.search.tableFilters = tableFilters;
    this.sidebar.performSearch();
  }

  /* Select/Check */

  async selectResultRow(row: SearchResult): Promise<void> {
    if (this.extendChecked$.value) {
      this.extendChecked$.next(false);
    }
    this.selectedResultId = row._id;
    const sessionId = row._source.session?._id;
    const subjectId = this.resultType === SearchReturnType.Subject ? row._source.subject?._id : null;
    const analysisLabel = this.resultType === SearchReturnType.Analysis ? row._source.analysis?.label : null;
    const acquisitionId = this.resultType === SearchReturnType.Acquisition ? row._source.acquisition?._id : null;

    this.sidebar.search.analysisLabel$.next(analysisLabel || '');
    this.sidebar.search.acqId$.next(acquisitionId || '');
    if (sessionId) {
      this.sidebar.search.selectedContainer$.next({ type: ContainerType.Session, id: sessionId });
    } else if (subjectId) {
      this.sidebar.search.selectedContainer$.next({ type: ContainerType.Subject, id: subjectId });
    } else if (analysisLabel) {
      const analysisParent = row._source.analysis.parent;
      const parentType = analysisParent.type === ContainerType.Subject ? ContainerType.Subject : ContainerType.Session;
      this.sidebar.search.selectedContainer$.next({ type: parentType, id: analysisParent.id })
    }

    let modalData = null;
    // Project attachments cannot be displayed in the SessionDetailDialog, link to the project that contains the file
    if (row._source.parent?.type === 'project') {
      const projectId = row._source.parent._id;
      const project = await this.projectQueryService.getProject(signal(projectId));

      modalData = { parentProject: project };
    }

    this.currentResultModal = this.dialog.open(SessionDetailDialog, {
      hasBackdrop: true,
      disableClose: false,
      height: '100%',
      width: '65%',
      panelClass: ['border-radius-0', 'padding-0'],
      position: { right: '0' },
      data: modalData,
    });
  }

  checkResultRow(row: SearchResult): void {
    if (this.resultType === SearchReturnType.Subject) {
      this.sessions.checkSubjects([row._source.subject?._id]);
    } else if (this.resultType === SearchReturnType.Session) {
      this.sessions.checkSessions([row._source.session?._id]);
    } else if (this.resultType === SearchReturnType.Acquisition) {
      const sessionId = row._source.session?._id;
      const status = this.sessions.getCheckedSessions().get(sessionId);
      const acquisitions = new Set(status?.acquisitions ?? []);
      const acquisitionId = row._source.acquisition?._id;
      if (acquisitions.has(acquisitionId)) {
        acquisitions.delete(acquisitionId);
      } else {
        acquisitions.add(acquisitionId);
      }
      this.sessions.checkAcquisitions({ _id: sessionId }, Array.from(acquisitions));
    } else {
      const checkedIds = new Set(this.checkedResultIds$.value);
      if (checkedIds.has(row._id)) {
        checkedIds.delete(row._id);
      } else {
        checkedIds.add(row._id);
      }
      this.checkedResultIds$.next(checkedIds);
    }
  }

  checkAllRows(checked?: boolean): void {
    const results = this.sidebar.search.results$.value;
    const resultIds = results.map(result => result._id);
    const checkedIds = this.checkedResultIds$.value;
    if (typeof checked !== 'boolean') {
      checked = !resultIds.every(resultId => checkedIds.has(resultId));
    }
    if (!checked && this.extendChecked$.value) {
      this.extendChecked$.next(false);
    }
    if (this.resultType === SearchReturnType.Subject) {
      this.sessions.clearCheckedSubjects();
      if (checked) {
        this.sessions.checkSubjects(resultIds, checked);
      }
    } else if (this.resultType === SearchReturnType.Session) {
      this.sessions.clearCheckedSessions();
      if (checked) {
        this.sessions.checkSessions(resultIds, checked);
      }
    } else if (this.resultType === SearchReturnType.Acquisition) {
      this.sessions.clearCheckedSessions();
      if (checked) {
        const sessions: { [sessionId: string]: string[] } = results.reduce((grouped, result) => {
          const sessionId = get(result, '_source.session._id');
          grouped[sessionId] = grouped[sessionId] || [];
          grouped[sessionId].push(get(result, '_source.acquisition._id'));
          return grouped;
        }, {});
        const updates = Object.entries(sessions).map(([sessionId, acquisitionIds]) => ({ session: { _id: sessionId }, acquisitionIds }));
        this.sessions.checkAcquisitionsBulk(updates);
      }
    } else {
      this.checkedResultIds$.next(new Set(checked ? resultIds : []));
    }
  }

  extendCheckedRows(): void {
    this.extendChecked$.next(true);
    const { total } = this.page;
    this.sidebar.performSearch(total).subscribe(() => {
      this.checkAllRows(true);
    });

    // Force a visual update to selection while the search runs
    const results = this.sidebar.search.results$.value;
    const resultIds = results.map(result => result._id);
    this.checkedResultIds$.next(new Set(resultIds));
  }

  getCheckedResults(ids?: Set<string>): SearchResult[] {
    const checkedIds = ids || this.checkedResultIds$.value;
    return this.sidebar.search.results$.value.filter(result => checkedIds.has(result._id));
  }

  /* Actions */

  private getContainerMetadata(containerType: ContainerType.Session | ContainerType.Subject): ContainerMetadata {
    return new Map(this.getCheckedResults().map(result => {
      const source = result._source[containerType];
      return [
        source?._id,
        {
          container_type: containerType,
          label: source?.label,
          project: result._source.project?._id,
        },
      ];
    }));
  }

  private getSessionMetadata(): ContainerMetadata {
    return this.getContainerMetadata(ContainerType.Session);
  }

  private getSubjectMetadata(): ContainerMetadata {
    return this.getContainerMetadata(ContainerType.Subject);
  }

  downloadFiles(): void {
    const checkedResults = this.getCheckedResults();
    if (this.resultType === SearchReturnType.Subject) {
      this.sessions.downloadCheckedFiles(this.getSubjectMetadata(), ContainerType.Subject);
    } else if ([SearchReturnType.Session, SearchReturnType.Acquisition].includes(this.resultType)) {
      this.sessions.downloadCheckedFiles(this.getSessionMetadata());
    } else if (this.resultType === SearchReturnType.Analysis) {
      this.coreApi.downloads.createTicket({
        nodes: checkedResults.map(result => ({ level: 'analysis', _id: result._source.analysis._id })),
        optional: true,
      }, { type: 'classic', prefix: 'flywheel' }).subscribe({
        next: ({ filename, ticket }) => {
          this.coreApi.downloads.downloadFile(filename, ticket);
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('download.analyses.error');
        },
      });
    } else if (this.resultType === SearchReturnType.File) {
      const files: DownloadFile[] = checkedResults.map(result => ({
        type: result._source.parent.type,
        id: result._source.parent._id,
        name: result._source.file.name,
      }));
      this.coreApi.downloads.createTicket({ files }, { type: 'bulk', prefix: 'flywheel' }).subscribe({
        next: ({ filename, ticket }) => {
          this.coreApi.downloads.downloadFile(filename, ticket);
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('download.files.error');
        },
      });
    }
  }

  modifyTags(): void {
    this.dialog.open(ModifyTagsDialog, {
      data: {
        checkedContainers: this.checkedResultIds$.value,
        resultType: this.resultType,
      },
    }).afterClosed().pipe(filter(result => Boolean(result)))
      .subscribe(() => {
        // refresh results
        this.sidebar.performSearch();
      });
  }

  getTagColor(tag: string): string {
    return '#' + stringToHexColor(tag);
  }

  areThereTags(): boolean {
    return this.searchFilters.get('tags').value.contains.length > 0 || this.searchFilters.get('tags').value.excludes.length > 0;
  }

  exportCSV(): void {
    const payload = this.sidebar.buildSearchQuery();
    const params = {
      facets: true,
      csv: true,
      size: Math.min(this.page?.total, MAX_DOWNLOAD_SIZE), // 10K is the limit on the API
    };
    this.downloading = true;

    this.coreApi.dataexplorer.searchExport(params, payload).subscribe(response => {
      downloadFile(`search_results_${new Date().toISOString().replace(/:/g, '.')}.csv`, 'data:text/csv;charset=utf-8,' + encodeURIComponent(response));
      this.downloading = false;
      this.cd.markForCheck();
    });
  }

  addToCollection(): void {
    this.sessions.addCheckedToCollection();
  }

  runBatchGear(): void {
    this.sessions.runBatchGear(this.getSessionMetadata());
  }

  async deleteContainers(): Promise<void> {
    // FIXME: Use allChecked$ once the endpoints support it
    const allChecked = false;// await firstValueFrom(this.allChecked$);
    let results: ContainerDeletionResults;
    if (allChecked) {
      const query = this.sidebar.buildSearchQuery();
      results = await this.sessions.confirmDeleteAll(query, this.getSessionMetadata());
    } else {
      results = await this.sessions.confirmDeleteChecked(this.getSessionMetadata());
    }
    if (results && results.successfulDeletions > 0) {
      this.filterResults();
    }
  }

  async moveSessionsToProject(): Promise<void> {
    const haveMoved = await this.sessions.moveSessionsToProject(this.getSessionMetadata());
    if (haveMoved) {
      this.filterResults();
    }
  }

  async moveSessionsToSubject(): Promise<void> {
    const haveMoved = await this.sessions.moveSessionsToSubject(this.getSessionMetadata());
    if (haveMoved) {
      this.filterResults();
    }
  }

  async createNewSession(): Promise<void> {
    const session = await this.sessions.createSession();
    if (session?._id) {
      this.filterResults();
    }
  }

  createTrainingSet(): void {
    const checkedResults = this.getCheckedResults();
    const payload: TrainingSetPayload = { file_count: 0 };
    if (checkedResults.length === 0) {
      payload.search_query = this.sidebar.buildSearchQuery();
      payload.file_count = this.sidebar.search.response?.facets?.facets?.file_count?.value || 0;
    } else {
      payload.files = checkedResults.map(file => ({ _id: file._id }));
      payload.file_count = payload.files.length;
    }
    this.dialog.open(TrainingSetDialog, { data: payload });
  }

  detailTabForResultType(selectedTab: string): string {
    switch (selectedTab) {
      case 'subjects':
        return 'subject';
      case 'sessions':
        return 'annotation';
      case 'acquisitions':
      case 'files':
        return 'data';
      case 'analyses':
        return 'analyses';
      default:
        return 'data';
    }
  }
}

function getDayRange(date: Date): { gte: string, lte: string } {
  // Query for the chosen day in the local browser timezone
  const min = new Date(date);
  min.setHours(0, 0, 0, 0);
  const max = new Date(date);
  max.setHours(23, 59, 59, 999);
  return {
    gte: min.toISOString(),
    lte: max.toISOString(),
  };
}
